import React from 'react';
import { Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import useAnswerDialog from '../../../hooks/useAnswerDialog';
import { FormTextarea } from '../../form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { generateAISolution, generateFollowUpResponse } from '../../../services/llmService';
import { Textarea } from '../../ui/textarea';

interface AnswerDialogProps {
  questionID: string;
  questionTitle: string;
}

/**
 * AnswerDialog component that allows users to submit answers in a dialog without redirecting.
 */
const AnswerDialog = ({ questionID, questionTitle }: AnswerDialogProps) => {
  const [open, setOpen] = React.useState(false);
  const [isGeneratingSolution, setIsGeneratingSolution] = React.useState(false);
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = React.useState(false);
  const [followUpOpen, setFollowUpOpen] = React.useState(false);
  const [followUpQuestion, setFollowUpQuestion] = React.useState('');
  const [previousResponse, setPreviousResponse] = React.useState('');
  const { text, textErr, setText, postAnswer, isSubmitting, resetForm } =
    useAnswerDialog(questionID);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    setOpen(isOpen);
  };

  const handleSubmit = async () => {
    const success = await postAnswer();
    if (success) {
      setOpen(false);
    }
  };

  const handleGenerateFollowUp = async () => {
    if (!followUpQuestion.trim()) return;
    setIsGeneratingFollowUp(true);
    try {
      const response = await generateFollowUpResponse(
        questionTitle,
        previousResponse,
        followUpQuestion,
      );
      setText(`**AI Response:**\n\n${response}\n\n${text}`);
      setFollowUpOpen(false);
      setFollowUpQuestion('');
    } catch (err) {
      // Handle error silently or show a toast notification
    } finally {
      setIsGeneratingFollowUp(false);
    }
  };

  const handleGenerateAISolution = async () => {
    setIsGeneratingSolution(true);
    try {
      const solution = await generateAISolution(questionTitle);
      const formattedSolution = `**AI Response:**\n\n${solution}`;
      setText(`${formattedSolution}\n\n${text}`);
      setPreviousResponse(solution);
    } catch (err) {
      // Handle error silently or show a toast notification
    } finally {
      setIsGeneratingSolution(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button variant='default' className='bg-primary'>
            Answer Question
          </Button>
        </DialogTrigger>
        <DialogContent className='sm:max-w-[800px] max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='text-xl'>Answer: {questionTitle}</DialogTitle>
          </DialogHeader>
          <div className='py-6'>
            <FormTextarea
              title='Your Answer'
              id='answerTextInput'
              val={text}
              setState={setText}
              err={textErr}
              mandatory={true}
              hint='Provide a clear, concise answer. You can use hyperlinks in the format [text](URL).'
            />
            {text && (
              <div className='mt-4 p-4 bg-slate-50 rounded-md overflow-x-hidden'>
                <div className='text-sm font-medium mb-2'>Preview:</div>
                <div className='prose prose-sm max-w-none break-words'>
                  <ReactMarkdown>{text}</ReactMarkdown>
                </div>
              </div>
            )}
            <div className='flex justify-end gap-2 mt-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={() => setFollowUpOpen(true)}
                disabled={!previousResponse}>
                Follow-up Question
              </Button>
              <Button
                variant='outline'
                size='sm'
                onClick={handleGenerateAISolution}
                disabled={isGeneratingSolution}>
                {isGeneratingSolution ? (
                  <>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    Generating...
                  </>
                ) : (
                  'Generate AI Answer'
                )}
              </Button>
            </div>
          </div>
          <DialogFooter className='flex flex-col sm:flex-row gap-2 sm:justify-between'>
            <DialogClose asChild>
              <Button variant='outline' className='sm:order-1'>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleSubmit}
              className='bg-blue-500 hover:bg-blue-600 sm:order-2'
              disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Submitting...
                </>
              ) : (
                'Post Answer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-up Question Dialog */}
      <Dialog open={followUpOpen} onOpenChange={setFollowUpOpen}>
        <DialogContent className='sm:max-w-[550px]'>
          <DialogHeader>
            <DialogTitle>Ask a Follow-up Question</DialogTitle>
          </DialogHeader>
          <div className='py-4'>
            <Textarea
              placeholder='Type your follow-up question here...'
              value={followUpQuestion}
              onChange={e => setFollowUpQuestion(e.target.value)}
              className='min-h-[100px]'
            />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setFollowUpOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerateFollowUp}
              disabled={isGeneratingFollowUp || !followUpQuestion.trim()}>
              {isGeneratingFollowUp ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Generating...
                </>
              ) : (
                'Generate Response'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnswerDialog;
